import axios from 'axios';
import axiosInstance from './axiosCreator';

export const CHAT_EXPORTS_ROUTE = '/api/v5/chat/exports';
const DOWNLOAD_EXPIRY_CLOCK_SKEW_MS = 60 * 60 * 1_000;

export type ChatExportDownloadFailure =
  'building' | 'expired' | 'not_found' | 'rate_limited' | 'failed';

export interface ChatExportDownload {
  url: string;
  filename: string;
  expiresAt: string;
}

export class ChatExportDownloadError extends Error {
  constructor(
    public readonly reason: ChatExportDownloadFailure,
    public readonly status: number | null = null
  ) {
    super(reason);
    this.name = 'ChatExportDownloadError';
  }
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const routeFor = (exportId: string, suffix = '') =>
  `${CHAT_EXPORTS_ROUTE}/${encodeURIComponent(exportId)}${suffix}`;

const toDownloadError = (error: unknown): ChatExportDownloadError => {
  if (!axios.isAxiosError(error)) return new ChatExportDownloadError('failed');

  const status = error.response?.status ?? null;

  if (status === 404) return new ChatExportDownloadError('not_found', status);
  if (status === 409) return new ChatExportDownloadError('building', status);
  if (status === 429)
    return new ChatExportDownloadError('rate_limited', status);

  return new ChatExportDownloadError('failed', status);
};

/** Ask the backend to build (when needed) and sign a fresh private download. */
export const requestChatExportDownload = async (
  exportId: string,
  signal?: AbortSignal
): Promise<ChatExportDownload> => {
  try {
    const response = await axiosInstance.post(
      routeFor(exportId, '/download'),
      undefined,
      { signal }
    );

    const payload = asRecord(response.data);
    const url = typeof payload?.url === 'string' ? payload.url : '';

    const filename =
      typeof payload?.filename === 'string' ? payload.filename : '';

    const expiresAt =
      typeof payload?.expiresAt === 'string' ? payload.expiresAt : '';

    const expiry = Date.parse(expiresAt);
    let protocol = '';

    try {
      protocol = new URL(url).protocol;
    } catch {
      // The malformed response is handled with the other required fields.
    }

    if (
      !url ||
      (protocol !== 'https:' && protocol !== 'http:') ||
      !filename ||
      !Number.isFinite(expiry)
    ) {
      throw new ChatExportDownloadError('failed');
    }

    // The server has just minted this URL. A student's device clock can be
    // wrong, so reject only a clearly stale response; S3 remains authoritative.
    if (expiry + DOWNLOAD_EXPIRY_CLOCK_SKEW_MS <= Date.now()) {
      throw new ChatExportDownloadError('expired');
    }

    return { url, filename, expiresAt };
  } catch (error) {
    if (error instanceof ChatExportDownloadError) throw error;
    throw toDownloadError(error);
  }
};
